/**
 * CRMRichiestePage — Gestione unificata "Richieste" (CRM-1 refactor + CRM-3).
 *
 * 1 sola tab per: lead pipeline + richieste da quotatori + import PDF.
 * Toggle vista: Kanban (pipeline) / Lista (filtri/search) / Import PDF (estrazione AI).
 * Mobile-first (colonne → tab orizzontali su <md). Auto-refresh 60s su Kanban.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Loader2, RefreshCw, AlertTriangle, User, Calendar, Euro, Inbox, FileText, Upload, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATI = [
  { id: "nuovo", label: "Nuovo", color: "bg-blue-50 border-blue-200 text-blue-700" },
  { id: "da_contattare", label: "Da contattare", color: "bg-amber-50 border-amber-200 text-amber-700" },
  { id: "contattato", label: "Contattato", color: "bg-cyan-50 border-cyan-200 text-cyan-700" },
  { id: "preventivo_inviato", label: "Preventivo inviato", color: "bg-violet-50 border-violet-200 text-violet-700" },
  { id: "trattativa", label: "Trattativa", color: "bg-orange-50 border-orange-200 text-orange-700" },
  { id: "convertito", label: "Convertito", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  { id: "perso", label: "Perso", color: "bg-zinc-100 border-zinc-200 text-zinc-600" },
];

const TIPO_RICHIESTA = {
  nuova_polizza: "Nuova polizza",
  rinnovo: "Rinnovo",
  assistenza: "Assistenza",
  sinistro: "Sinistro",
  documenti: "Documenti",
  modifica_dati: "Modifica dati",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const giorni = Math.floor(ms / 86400000);
  if (giorni > 0) return `${giorni}g fa`;
  const ore = Math.floor(ms / 3600000);
  if (ore > 0) return `${ore}h fa`;
  const min = Math.floor(ms / 60000);
  return `${Math.max(1, min)}m fa`;
}

function _customerDisplayName(lead) {
  // Quick-win feb 2026: fallback robusto nome -> email -> anonimo
  const c = lead.customer_label;
  if (c && c.trim() && c.toLowerCase() !== "—" && c.toLowerCase() !== "lead") return c;
  const cl = lead.contact_label;
  if (cl && cl.trim()) return cl;
  const email = lead.contact_email || lead.email;
  if (email) return email;
  return `Lead anonimo #${(lead.id || "").slice(0, 6)}`;
}

// ===========================================================================
// CRM-6.3 Preventivass — bottone "Apri Preventivass" + modal Importa preventivo
// ===========================================================================

// URL builder: pre-compila parametri Preventivass.it dal lead. I parametri
// reali del portale IVASS non sono pubblicamente documentati (form interno);
// in mancanza, costruiamo un URL base + il riepilogo viene mostrato in un
// pannello "Copia dati per Preventivass" che il consulente puo' incollare.
function buildPreventivassUrl(lead, customer) {
  // Base URL fissa Preventivass.it
  const base = "https://www.preventivass.it/home";
  // Pre-compilazione query string lato URL (best-effort: se i nomi parametri
  // ufficiali sono diversi, almeno arriva al portale con i dati a portata).
  const sub = lead.submission_data || {};
  const ana = (customer && customer.anagrafica) || {};
  const params = new URLSearchParams();
  if (sub.codice_fiscale || ana.codice_fiscale) {
    params.set("cf", sub.codice_fiscale || ana.codice_fiscale);
  }
  if (sub.targa) params.set("targa", sub.targa);
  if (sub.data_nascita || ana.data_nascita) params.set("dn", sub.data_nascita || ana.data_nascita);
  if (sub.cap || ana.cap) params.set("cap", sub.cap || ana.cap);
  if (sub.classe_cu || sub.classe_universale) params.set("cu", sub.classe_cu || sub.classe_universale);
  if (sub.data_decorrenza) params.set("decorrenza", sub.data_decorrenza);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function PreventivassRiepilogoCopy({ lead, customer }) {
  const sub = lead.submission_data || {};
  const ana = (customer && customer.anagrafica) || {};
  const lines = [
    `=== Dati lead per portale benchmark ===`,
    `Lead ID: ${lead.id}`,
    `Codice fiscale: ${sub.codice_fiscale || ana.codice_fiscale || "(da richiedere)"}`,
    `Nome cognome: ${ana.nome || sub.nome || ""} ${ana.cognome || sub.cognome || ""}`.trim(),
    `Data nascita: ${sub.data_nascita || ana.data_nascita || "(da richiedere)"}`,
    `Sesso: ${sub.sesso || ana.sesso || "(da richiedere)"}`,
    `Residenza: ${sub.indirizzo_residenza || ""} ${sub.cap || ana.cap || ""} ${sub.comune_residenza || sub.citta || ""} ${sub.provincia || ""}`.trim(),
    `Professione: ${sub.professione || "(da richiedere)"}`,
    `Stato civile: ${sub.stato_civile || "(da richiedere)"}`,
    `Titolo studio: ${sub.titolo_studio || "(da richiedere)"}`,
    ``,
    `--- Veicolo ---`,
    `Targa: ${sub.targa || "(da richiedere)"}`,
    `Marca-Modello: ${sub.marca || ""} ${sub.modello || ""} ${sub.allestimento || ""}`.trim(),
    `Anno immatricolazione: ${sub.anno_immatricolazione || "(da richiedere)"}`,
    `Mese immatricolazione: ${sub.mese_immatricolazione || ""}`,
    `Alimentazione: ${sub.alimentazione || "(da richiedere)"}`,
    `Cilindrata: ${sub.cilindrata || ""} cc`,
    `Potenza KW: ${sub.kw || ""}`,
    `Uso: ${sub.uso_veicolo || "privato"}`,
    `Percorrenza annua: ${sub.percorrenza_annua_km || "(da richiedere)"} km`,
    `Antifurto: ${sub.antifurto || "no"}`,
    ``,
    `--- Storia assicurativa ---`,
    `Tipo richiesta: ${sub.tipo_operazione || "nuova polizza"}`,
    `Compagnia attuale: ${sub.compagnia_attuale || ""}`,
    `Scadenza polizza attuale: ${sub.scadenza_polizza || ""}`,
    `Data decorrenza nuova: ${sub.data_decorrenza || ""}`,
    `Classe CU attuale: ${sub.classe_cu || sub.classe_universale || "(da richiedere)"}`,
    `Classe assegnazione: ${sub.classe_assegnazione || ""}`,
    `Sinistri ultimi 5 anni: ${sub.sinistri_5anni_numero ?? sub.sinistri_fatti ?? 0}`,
    `Anni assicurazione: ${sub.anni_assicurazione_continuativa || ""}`,
    `Formula guida: ${sub.formula_guida || "libera"}`,
    ``,
    `--- Coperture richieste ---`,
    `Massimale RCA: € ${sub.massimale_rca || "6.000.000"}`,
    `Furto e incendio: ${sub.cop_furto_incendio ? "sì" : "no"}`,
    `Kasko: ${sub.cop_kasko ? "sì" : "no"}`,
    `Cristalli: ${sub.cop_cristalli ? "sì" : "no"}`,
    `Assistenza stradale: ${sub.cop_assistenza ? "sì" : "no"}`,
    `Tutela legale: ${sub.cop_tutela_legale ? "sì" : "no"}`,
    `Infortuni conducente: ${sub.cop_infortuni ? "sì" : "no"}`,
    `Atti vandalici: ${sub.cop_vandalici ? "sì" : "no"}`,
    `Eventi naturali: ${sub.cop_naturali ? "sì" : "no"}`,
  ];
  return lines.join("\n");
}

function PreventivassImportModal({ lead, onClose, onImported }) {
  const [codice, setCodice] = useState("");
  const [dataPrev, setDataPrev] = useState(new Date().toISOString().slice(0, 10));
  const [pasted, setPasted] = useState("");
  const [parsed, setParsed] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Parser semplice: ogni riga "Compagnia | premio [| massimale [| franchigia]]"
  // Separatori accettati: |  ;  tab  più spazi consecutivi
  const handleParse = () => {
    setError(null);
    const rows = [];
    pasted.split(/\r?\n/).forEach((line) => {
      const t = line.trim();
      if (!t) return;
      const parts = t.split(/\s*[\|;\t]\s*|\s{2,}/).filter(Boolean);
      if (parts.length < 2) return;
      const num = (s) => {
        if (!s) return null;
        const cleaned = String(s).replace(/[€$\s]/g, "").replace(",", ".");
        const v = parseFloat(cleaned);
        return isNaN(v) ? null : v;
      };
      const compagnia = parts[0];
      const premio = num(parts[1]);
      if (!compagnia || premio === null) return;
      rows.push({
        compagnia_legal: compagnia,
        premio_annuo: premio,
        massimale_rcauto: num(parts[2]),
        franchigia: num(parts[3]) || 0,
      });
    });
    if (rows.length === 0) {
      setError("Nessuna riga valida riconosciuta. Formato atteso: Compagnia | premio €");
      return;
    }
    setParsed(rows);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/api/admin/crm/market-appetite/import-preventivass`, {
        richiesta_id: lead.id,
        codice_preventivass: codice.trim(),
        data_preventivo: dataPrev,
        validita_giorni: 60,
        righe_premi: parsed,
        raw_paste: pasted,
      }, { withCredentials: true });
      onImported(r.data);
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}
         data-testid="crm-preventivass-modal">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zinc-900">📥 Importa preventivo benchmark</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900">✕</button>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-zinc-700">Codice preventivo benchmark (60gg validità)</span>
          <input value={codice} onChange={(e) => setCodice(e.target.value)}
            placeholder="es. ABC123XYZ" data-testid="crm-pv-codice"
            className="w-full mt-0.5 px-2 py-1.5 rounded border border-zinc-200 text-sm" />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-zinc-700">Data preventivo</span>
          <input type="date" value={dataPrev} onChange={(e) => setDataPrev(e.target.value)}
            data-testid="crm-pv-data"
            className="w-full mt-0.5 px-2 py-1.5 rounded border border-zinc-200 text-sm" />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-zinc-700">
            Tabella premi (1 riga per compagnia, separati da <code>|</code>)
          </span>
          <textarea value={pasted} onChange={(e) => setPasted(e.target.value)}
            rows={8} data-testid="crm-pv-pasted"
            placeholder={"Great Lakes Insurance SE | 543.20 | 6000000\nZavarovalnica Triglav, d.d. | 612.40\nReale Mutua Assicurazioni | 678.50 | 6000000 | 250\nGenertel | 520.00\n…"}
            className="w-full mt-0.5 px-2 py-1.5 rounded border border-zinc-200 text-xs font-mono" />
        </label>

        <button onClick={handleParse} disabled={!pasted.trim()} data-testid="crm-pv-parse-btn"
          className="w-full px-3 py-1.5 text-xs rounded bg-zinc-100 hover:bg-zinc-200 disabled:opacity-50">
          Analizza incolla ({pasted.split(/\r?\n/).filter(Boolean).length} righe)
        </button>

        {parsed.length > 0 && (
          <div className="bg-emerald-50 rounded p-2 text-xs space-y-1" data-testid="crm-pv-preview">
            <p className="font-semibold text-emerald-800">✓ {parsed.length} compagnie riconosciute</p>
            <ol className="ml-4 list-decimal space-y-0.5">
              {parsed.slice().sort((a, b) => a.premio_annuo - b.premio_annuo).map((r, i) => (
                <li key={i} className="text-emerald-900">
                  <span className="font-medium">{r.compagnia_legal}</span> — €{r.premio_annuo}
                </li>
              ))}
            </ol>
          </div>
        )}

        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-800"
               data-testid="crm-pv-error">{error}</div>
        )}

        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2 text-sm rounded bg-zinc-100 hover:bg-zinc-200">Annulla</button>
          <button onClick={handleSubmit}
            disabled={!codice.trim() || parsed.length === 0 || submitting}
            data-testid="crm-pv-submit-btn"
            className="flex-1 py-2 text-sm rounded bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50">
            {submitting ? "Salvataggio..." : "Salva import"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreventivassSection({ lead, customer, onAppetiteRefresh }) {
  const [showImport, setShowImport] = useState(false);
  const [imports, setImports] = useState([]);
  const [showRiepilogo, setShowRiepilogo] = useState(false);

  const loadImports = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/crm/preventivass-imports/${lead.id}`,
        { withCredentials: true });
      setImports(r.data.items || []);
    } catch { /* silent */ }
  }, [lead.id]);

  useEffect(() => { loadImports(); }, [loadImports]);

  // Solo per lead RC Auto: la sezione Preventivass ha senso qui
  const isAuto = (lead.tipologia || "").toLowerCase() === "auto";
  if (!isAuto) return null;

  const pvUrl = buildPreventivassUrl(lead, customer);
  const latest = imports[0];

  const handleCopia = () => {
    const txt = PreventivassRiepilogoCopy({ lead, customer });
    navigator.clipboard.writeText(txt).then(
      () => alert("Riepilogo copiato negli appunti. Incollalo dove ti serve."),
      () => alert("Impossibile copiare. Seleziona manualmente il testo sotto."),
    );
  };

  return (
    <div className="mt-5 border-t border-zinc-100 pt-4" data-testid="crm-preventivass-section">
      <p className="text-sm font-semibold text-zinc-800 mb-2 flex items-center gap-1.5">
        <FileText className="w-4 h-4" /> Confronto benchmark
        <span className="text-[10px] font-normal text-zinc-400">v3</span>
      </p>

      <div className="flex flex-col gap-1.5">
        <a href={pvUrl} target="_blank" rel="noreferrer" data-testid="crm-pv-open-btn"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded bg-orange-500 text-white hover:bg-orange-600">
          🔗 Apri portale benchmark
        </a>
        <button onClick={() => setShowImport(true)} data-testid="crm-pv-import-btn"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded bg-zinc-900 text-white hover:bg-zinc-800">
          📥 Importa preventivo
        </button>
        <button onClick={() => setShowRiepilogo((s) => !s)} data-testid="crm-pv-riepilogo-toggle"
          className="text-[11px] text-zinc-500 underline self-start mt-1">
          {showRiepilogo ? "Nascondi" : "Mostra"} riepilogo dati per portale benchmark
        </button>
        {showRiepilogo && (
          <>
            <pre className="text-[10px] bg-zinc-50 p-2 rounded font-mono overflow-x-auto max-h-48 overflow-y-auto"
                 data-testid="crm-pv-riepilogo">{PreventivassRiepilogoCopy({ lead, customer })}</pre>
            <button onClick={handleCopia} className="text-[11px] text-zinc-700 underline self-start"
                    data-testid="crm-pv-copia-btn">📋 Copia riepilogo</button>
          </>
        )}
      </div>

      {latest && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded p-2 text-xs"
             data-testid="crm-pv-latest">
          <p className="font-semibold text-emerald-800">
            🟢 Benchmark importato il {new Date(latest.data_import).toLocaleString("it-IT")}
          </p>
          <p className="text-emerald-700">
            Codice <code className="bg-white px-1 py-0.5 rounded">{latest.codice_preventivass}</code> ·
            <span className="ml-1">{latest.righe_premi?.length || 0} compagnie</span> ·
            <span className="ml-1">scade {latest.scade_il?.slice(0, 10)}</span>
          </p>
          <div className="mt-1.5 max-h-32 overflow-y-auto">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-emerald-700"><th className="text-left">Compagnia</th><th className="text-right">Premio</th></tr>
              </thead>
              <tbody>
                {(latest.righe_premi || []).slice().sort((a, b) => a.premio_annuo - b.premio_annuo).map((r, i) => {
                  const norm = r.compagnia_legal.toLowerCase();
                  const isPrima = /great lakes|triglav/.test(norm);
                  const isRM = /reale mutua/.test(norm);
                  return (
                    <tr key={i} className={isPrima ? "bg-amber-50 font-semibold" : isRM ? "bg-violet-50 font-semibold" : ""}>
                      <td>{i + 1}. {r.compagnia_legal}{isPrima ? " 🅿️" : ""}{isRM ? " 🆁" : ""}</td>
                      <td className="text-right">€ {r.premio_annuo.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {latest.aggregato && (
            <p className="text-[10px] text-emerald-600 mt-1">
              Prima aggregato: € {latest.aggregato.premio_prima_great_lakes?.toFixed(2) ?? "—"} (pos {latest.aggregato.posizione_prima_great_lakes || "—"}) ·
              RM: € {latest.aggregato.premio_reale_mutua?.toFixed(2) ?? "—"} (pos {latest.aggregato.posizione_reale_mutua || "—"})
            </p>
          )}
          {latest.market_appetite_snapshot && (
            <p className="text-[10px] text-emerald-700 mt-1 font-semibold">
              Market Appetite: <strong>{latest.market_appetite_snapshot.fit_score}/100</strong> ({latest.market_appetite_snapshot.tier})
            </p>
          )}
        </div>
      )}

      {showImport && (
        <PreventivassImportModal lead={lead} onClose={() => setShowImport(false)}
          onImported={(rec) => {
            setImports((prev) => [rec, ...prev]);
            if (onAppetiteRefresh) onAppetiteRefresh(rec.market_appetite_snapshot);
          }} />
      )}
    </div>
  );
}



const TIPOLOGIA_BADGE = {
  auto: "bg-orange-100 text-orange-700",
  casa: "bg-emerald-100 text-emerald-700",
  vita: "bg-purple-100 text-purple-700",
  aziende: "bg-blue-100 text-blue-700",
  sanitario: "bg-rose-100 text-rose-700",
  viaggio: "bg-amber-100 text-amber-700",
  commercio: "bg-cyan-100 text-cyan-700",
  fondi_pensione: "bg-indigo-100 text-indigo-700",
  convenzione: "bg-pink-100 text-pink-700",
  catnat: "bg-stone-100 text-stone-700",
  rischi_salute: "bg-rose-100 text-rose-700",
};

function LeadCard({ lead, onDragStart, onClick }) {
  const displayName = _customerDisplayName(lead);
  const tipologia = (lead.tipologia || lead.tipo_richiesta || "—").toLowerCase();
  const badgeClass = TIPOLOGIA_BADGE[tipologia] || "bg-zinc-100 text-zinc-700";
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={() => onClick(lead)}
      data-testid={`crm-lead-card-${lead.id}`}
      className="bg-white rounded-lg border border-zinc-200 p-3 hover:border-zinc-900/40 hover:shadow-sm transition-all cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <p className="text-sm font-semibold text-zinc-900 truncate flex-1" data-testid={`crm-lead-name-${lead.id}`}>
          {displayName}
        </p>
        <span className="text-[10px] text-zinc-400 whitespace-nowrap" data-testid={`crm-lead-age-${lead.id}`}>
          {timeAgo(lead.created_at || lead.updated_at)}
        </span>
      </div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${badgeClass}`}
              data-testid={`crm-lead-tipologia-${lead.id}`}>
          {tipologia}
        </span>
        <span className="text-[10px] text-zinc-500">
          {TIPO_RICHIESTA[lead.tipo_richiesta] || lead.tipo_richiesta || ""}
        </span>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-zinc-500">
        {lead.premio_stimato_eur != null && (
          <span className="inline-flex items-center gap-0.5">
            <Euro className="w-3 h-3" />{Math.round(lead.premio_stimato_eur)}
          </span>
        )}
        {lead.operatore_assegnato_id && (
          <span className="inline-flex items-center gap-0.5 truncate">
            <User className="w-3 h-3" />{lead.operatore_assegnato_id.split("@")[0]}
          </span>
        )}
        {lead.market_appetite_last?.fit_score != null && (
          <span className="ml-auto inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-zinc-100 text-zinc-700 font-semibold"
                data-testid={`crm-lead-fit-${lead.id}`}>
            MA {lead.market_appetite_last.fit_score}
          </span>
        )}
      </div>
    </div>
  );
}

export default function CRMRichiestePage() {
  const [vista, setVista] = useState("kanban");  // kanban | lista | import_pdf
  const [leadsByStato, setLeadsByStato] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMobileTab, setActiveMobileTab] = useState("nuovo");
  const [filtroOperatore, setFiltroOperatore] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("per_page", "100");
      if (filtroOperatore) params.set("operatore_id", filtroOperatore);
      if (filtroTipo) params.set("tipo_richiesta", filtroTipo);
      const r = await axios.get(`${API}/api/admin/crm/leads?${params.toString()}`, { withCredentials: true });
      const grouped = STATI.reduce((acc, s) => ({ ...acc, [s.id]: [] }), {});
      for (const lead of r.data.items) {
        const k = lead.stato_pipeline || "nuovo";
        if (!grouped[k]) grouped[k] = [];
        grouped[k].push(lead);
      }
      setLeadsByStato(grouped);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [filtroOperatore, filtroTipo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const handleDragStart = (e, lead) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ id: lead.id, from: lead.stato_pipeline }));
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDrop = async (e, statoTarget) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    let info;
    try { info = JSON.parse(raw); } catch { return; }
    if (info.from === statoTarget) return;
    try {
      await axios.patch(`${API}/api/admin/crm/leads/${info.id}`,
        { stato_pipeline: statoTarget }, { withCredentials: true });
      await load();
    } catch (err) {
      // FASE 3.5b — override-ack quando admin tocca lead di intermediario
      if (err.response?.status === 409 &&
          err.response?.data?.detail?.error_code === "INTERMEDIARIO_OVERRIDE_REQUIRED") {
        const interm = err.response.data.detail.intermediario || {};
        const ok = window.confirm(
          `Questa richiesta è gestita dall'intermediario "${interm.ragione_sociale || '(sconosciuto)'}". ` +
          `L'intermediario sarà notificato. Procedere comunque?`
        );
        if (!ok) return;
        try {
          await axios.patch(`${API}/api/admin/crm/leads/${info.id}`,
            { stato_pipeline: statoTarget, intermediario_override_acknowledged: true },
            { withCredentials: true });
          await load();
          return;
        } catch (e2) {
          alert("Spostamento fallito: " + (e2.response?.data?.detail?.message || e2.message));
          return;
        }
      }
      alert("Spostamento fallito: " + (err.response?.data?.detail?.message || err.message));
    }
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const totale = useMemo(
    () => Object.values(leadsByStato).reduce((sum, arr) => sum + arr.length, 0),
    [leadsByStato]
  );

  return (
    <div className="space-y-4" data-testid="crm-richieste-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
            <Inbox className="w-5 h-5" /> Richieste
          </h2>
          <p className="text-sm text-zinc-500">{totale} richieste in pipeline · auto-refresh 60s</p>
        </div>
        <div className="flex items-center gap-2">
        <a href={`${API}/api/admin/crm/export/richieste?format=xlsx`} target="_blank" rel="noreferrer"
           data-testid="crm-richieste-export-btn"
           className="px-3 py-1.5 text-xs rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
          Export
        </a>
        <div className="flex gap-1 bg-zinc-100 rounded p-0.5 text-xs">
          {[
            { k: "kanban", l: "Kanban" }, { k: "lista", l: "Lista" }, { k: "import_pdf", l: "Importa PDF" },
          ].map(v => (
            <button key={v.k} onClick={() => setVista(v.k)} data-testid={`crm-richieste-vista-${v.k}`}
              className={`px-3 py-1.5 rounded ${vista === v.k ? "bg-white shadow font-semibold" : "text-zinc-500"}`}>
              {v.l}
            </button>
          ))}
        </div>
        </div>
      </div>

      {vista === "import_pdf" && <ImportPdfPanel onCreated={() => { setVista("kanban"); load(); }} />}
      {vista === "lista" && <ListaView />}
      {vista === "kanban" && (
      <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
        </div>
        <div className="flex items-center gap-2">
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
            data-testid="crm-leads-filter-tipo"
            className="text-sm px-2 py-1.5 rounded-lg border border-zinc-200 bg-white">
            <option value="">Tutti i tipi</option>
            {Object.entries(TIPO_RICHIESTA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input value={filtroOperatore} onChange={(e) => setFiltroOperatore(e.target.value)}
            placeholder="Operatore..." data-testid="crm-leads-filter-operatore"
            className="text-sm px-2 py-1.5 rounded-lg border border-zinc-200 w-32" />
          <button onClick={load} disabled={loading} data-testid="crm-leads-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700 flex items-center gap-2" data-testid="crm-leads-error">
          <AlertTriangle className="w-4 h-4" /> {String(error)}
        </div>
      )}

      {/* Mobile: tab orizzontali */}
      <div className="md:hidden flex gap-1 overflow-x-auto pb-1" data-testid="crm-leads-mobile-tabs">
        {STATI.map((s) => (
          <button key={s.id} onClick={() => setActiveMobileTab(s.id)} data-testid={`crm-leads-tab-${s.id}`}
            className={`whitespace-nowrap px-2.5 py-1.5 text-xs rounded-lg border transition-all ${
              activeMobileTab === s.id ? `${s.color} border-current font-semibold` : "bg-zinc-50 border-zinc-200 text-zinc-600"
            }`}>
            {s.label} ({(leadsByStato[s.id] || []).length})
          </button>
        ))}
      </div>

      {/* Desktop: 7 colonne */}
      <div className="hidden md:grid md:grid-cols-7 gap-3 min-h-[400px]" data-testid="crm-leads-kanban">
        {STATI.map((s) => {
          const cards = leadsByStato[s.id] || [];
          return (
            <div key={s.id} onDrop={(e) => handleDrop(e, s.id)} onDragOver={handleDragOver}
              data-testid={`crm-kanban-col-${s.id}`}
              className={`rounded-xl border-2 ${s.color} p-2 min-h-[400px]`}>
              <div className="flex items-center justify-between mb-2 px-1">
                <p className="text-xs font-semibold uppercase tracking-wide">{s.label}</p>
                <span className="text-[10px] font-mono">{cards.length}</span>
              </div>
              <div className="space-y-2">
                {cards.map((l) => (
                  <LeadCard key={l.id} lead={l} onDragStart={handleDragStart} onClick={setSelectedLead} />
                ))}
                {cards.length === 0 && <p className="text-[11px] text-zinc-400 italic text-center py-3">vuoto</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: cards di colonna attiva */}
      <div className="md:hidden space-y-2" data-testid="crm-leads-mobile-list">
        {(leadsByStato[activeMobileTab] || []).map((l) => (
          <LeadCard key={l.id} lead={l} onDragStart={handleDragStart} onClick={setSelectedLead} />
        ))}
        {(leadsByStato[activeMobileTab] || []).length === 0 && (
          <p className="text-sm text-zinc-400 italic text-center py-6">Nessun lead in questo stato</p>
        )}
      </div>

      {/* Drawer dettaglio lead */}
      {selectedLead && (
        <LeadDetailDrawer leadId={selectedLead.id} onClose={() => { setSelectedLead(null); load(); }} />
      )}
      </div>
      )}
    </div>
  );
}

// ============================================================================
// VISTA LISTA — placeholder semplice (eredita filtri da pipeline futuri)
// ============================================================================
function ListaView() {
  return (
    <div className="text-sm text-zinc-500 italic p-6 text-center border border-dashed border-zinc-200 rounded">
      Vista lista — useful per ricerca/filtri avanzati. Verrà ampliata in CRM-4.
    </div>
  );
}

// ============================================================================
// IMPORT PDF — estrazione AI + creazione customer+polizza
// ============================================================================
function ImportPdfPanel({ onCreated }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const submit = async () => {
    if (!file) return;
    setLoading(true); setError(null); setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/api/admin/crm/documents/import-pdf-polizza`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtracted(r.data);
    } catch (e) {
      setError(e.response?.data?.detail?.msg || e.response?.data?.detail?.error_code || e.message);
    } finally { setLoading(false); }
  };

  const createCustomerAndPolicy = async () => {
    if (!extracted) return;
    setCreating(true); setError(null);
    try {
      const anag = extracted.extracted_anagrafica || {};
      const pol = extracted.extracted_polizza || {};
      // 1) Create customer
      let customer_id = null;
      try {
        const rc = await axios.post(`${API}/api/admin/crm/customers`, {
          tipo: anag.partita_iva ? "azienda" : "privato",
          nome: anag.nome, cognome: anag.cognome,
          ragione_sociale: anag.ragione_sociale,
          codice_fiscale: anag.codice_fiscale, partita_iva: anag.partita_iva,
          email: anag.email, telefono: anag.telefono,
        }, { withCredentials: true });
        customer_id = rc.data.id;
      } catch (e) {
        if (e.response?.status === 409) {
          customer_id = e.response.data?.detail?.customer_id;
        } else throw e;
      }
      // 2) Create policy (se dati polizza presenti)
      if (pol.numero_polizza && pol.scadenza) {
        await axios.post(`${API}/api/admin/crm/policies`, {
          customer_id,
          compagnia: pol.compagnia || "Da definire",
          ramo: pol.ramo || "altro",
          numero_polizza: pol.numero_polizza,
          decorrenza: pol.decorrenza || pol.scadenza,
          scadenza: pol.scadenza,
          premio_lordo_eur: pol.premio_lordo_eur || 0,
          tipo_emissione: "nuova",
          tipologia_gestione: "polizza_concorrente",
          note: `Importata da PDF doc_id=${extracted.document_id}`,
        }, { withCredentials: true });
      }
      alert("Cliente + polizza importati. Apri il drawer cliente per verificare.");
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail?.error_code || e.message);
    } finally { setCreating(false); }
  };

  return (
    <div className="border border-zinc-200 rounded-xl p-4 space-y-3" data-testid="crm-import-pdf-panel">
      <p className="text-sm flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-violet-600" />
        Carica un PDF (polizza, documento identità, modulo). L'AI estrae anagrafica + dati polizza.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="file" accept=".pdf,application/pdf" onChange={(e) => setFile(e.target.files?.[0])}
          className="text-xs flex-1" data-testid="crm-import-pdf-file" />
        <button onClick={submit} disabled={!file || loading}
          className="px-3 py-2 text-sm bg-zinc-900 text-white rounded disabled:opacity-50 flex items-center gap-1"
          data-testid="crm-import-pdf-submit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Estrai dati
        </button>
      </div>
      {error && <p className="text-xs text-rose-600">{String(error)}</p>}
      {extracted && (
        <div className="bg-zinc-50 rounded p-3 text-xs space-y-2" data-testid="crm-import-pdf-result">
          <div>
            <p className="font-semibold mb-1">Anagrafica estratta</p>
            <pre className="bg-white p-2 rounded text-[10px] overflow-x-auto">
{JSON.stringify(extracted.extracted_anagrafica, null, 2)}
            </pre>
          </div>
          <div>
            <p className="font-semibold mb-1">Polizza estratta</p>
            <pre className="bg-white p-2 rounded text-[10px] overflow-x-auto">
{JSON.stringify(extracted.extracted_polizza, null, 2)}
            </pre>
          </div>
          {extracted.confidence_scores && (
            <p className="text-zinc-500">
              Confidence anagrafica: {((extracted.confidence_scores.anagrafica || 0) * 100).toFixed(0)}% ·
              polizza: {((extracted.confidence_scores.polizza || 0) * 100).toFixed(0)}%
            </p>
          )}
          <button onClick={createCustomerAndPolicy} disabled={creating}
            className="w-full py-2 text-sm bg-emerald-600 text-white rounded disabled:opacity-50"
            data-testid="crm-import-pdf-create-btn">
            {creating ? "Creazione..." : "Crea cliente + polizza (concorrente)"}
          </button>
        </div>
      )}
    </div>
  );
}

function MarketAppetiteForm({ leadId, onComputed }) {
  const [form, setForm] = useState({
    rating_colucci: 60, fascia_rischio: "media",
    sinistri_fatti: 0, sinistri_subiti: 0,
    classe_cu: 6, anni_assicurativi: 5,
    premio_minimo_mercato: 600, compagnia_piu_bassa: "Prima",
    premio_prima_great_lakes: 620, posizione_prima_great_lakes: 2,
    premio_reale_mutua: 720, posizione_reale_mutua: 5,
    premio_medio_mercato: 780, numero_compagnie_quotanti: 12,
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...form, richiesta_id: leadId };
      // Cast numerici
      ["rating_colucci","sinistri_fatti","sinistri_subiti","classe_cu","anni_assicurativi",
       "premio_minimo_mercato","premio_prima_great_lakes","posizione_prima_great_lakes",
       "premio_reale_mutua","posizione_reale_mutua","premio_medio_mercato",
       "numero_compagnie_quotanti"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      const r = await axios.post(`${API}/api/admin/crm/market-appetite/compute`,
        payload, { withCredentials: true });
      onComputed(r.data);
    } catch (e) {
      alert("Errore calcolo: " + (e.response?.data?.detail
        ? JSON.stringify(e.response.data.detail) : e.message));
    } finally { setLoading(false); }
  };

  const num = (k, label, props = {}) => (
    <label className="block text-[11px]">
      <span className="text-zinc-600">{label}</span>
      <input type="number" value={form[k] ?? ""} {...props}
        data-testid={`ma-input-${k}`}
        onChange={(e) => update(k, e.target.value)}
        className="w-full mt-0.5 px-2 py-1 rounded border border-zinc-200 text-xs" />
    </label>
  );

  return (
    <div className="space-y-2 bg-zinc-50 rounded p-2 text-xs" data-testid="crm-appetite-form">
      <div className="grid grid-cols-2 gap-2">
        {num("rating_colucci", "Rating Colucci (1-100)", { min: 1, max: 100 })}
        <label className="block text-[11px]">
          <span className="text-zinc-600">Fascia rischio</span>
          <select value={form.fascia_rischio} onChange={(e) => update("fascia_rischio", e.target.value)}
            data-testid="ma-input-fascia_rischio"
            className="w-full mt-0.5 px-2 py-1 rounded border border-zinc-200 text-xs">
            <option value="bassa">bassa</option>
            <option value="media">media</option>
            <option value="alta">alta</option>
            <option value="molto_alta">molto_alta</option>
          </select>
        </label>
        {num("sinistri_fatti", "Sinistri fatti", { min: 0 })}
        {num("sinistri_subiti", "Sinistri subiti", { min: 0 })}
        {num("classe_cu", "Classe CU", { min: 1, max: 18 })}
        {num("anni_assicurativi", "Anni assicurativi", { min: 0 })}
        {num("premio_minimo_mercato", "Premio minimo mercato (€)", { min: 0, step: 10 })}
        <label className="block text-[11px]">
          <span className="text-zinc-600">Compagnia + bassa</span>
          <input type="text" value={form.compagnia_piu_bassa}
            data-testid="ma-input-compagnia_piu_bassa"
            onChange={(e) => update("compagnia_piu_bassa", e.target.value)}
            className="w-full mt-0.5 px-2 py-1 rounded border border-zinc-200 text-xs" />
        </label>
        {num("premio_prima_great_lakes", "Premio Prima/GL (€)", { min: 0, step: 10 })}
        {num("posizione_prima_great_lakes", "Posizione Prima", { min: 1, max: 50 })}
        {num("premio_reale_mutua", "Premio Reale Mutua (€)", { min: 0, step: 10 })}
        {num("posizione_reale_mutua", "Posizione RM", { min: 1, max: 50 })}
        {num("premio_medio_mercato", "Premio medio mercato (€)", { min: 0, step: 10 })}
        {num("numero_compagnie_quotanti", "N° compagnie quotanti", { min: 1, max: 50 })}
      </div>
      <button onClick={submit} disabled={loading} data-testid="ma-compute-submit"
        className="w-full px-3 py-1.5 text-xs rounded bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50">
        {loading ? "Calcolo..." : "Calcola Market Appetite"}
      </button>
    </div>
  );
}


function LeadDetailDrawer({ leadId, onClose }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [appetite, setAppetite] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/admin/crm/leads/${leadId}`, { withCredentials: true })
      .then((r) => setData(r.data));
    axios.get(`${API}/api/admin/crm/market-appetite/${leadId}`, { withCredentials: true })
      .then((r) => { if (r.data?.available) setAppetite(r.data.snapshot); })
      .catch(() => {});
  }, [leadId]);

  const aggiungiNota = async () => {
    if (!note.trim()) return;
    await axios.patch(`${API}/api/admin/crm/leads/${leadId}`, { nota: note }, { withCredentials: true });
    setNote("");
    const r = await axios.get(`${API}/api/admin/crm/leads/${leadId}`, { withCredentials: true });
    setData(r.data);
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/crm/market-appetite/${leadId}/history`,
        { withCredentials: true });
      setHistory(r.data.history || []);
      setShowHistory(true);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const tierColors = {
    hot: "bg-rose-50 border-rose-200 text-rose-700",
    warm: "bg-amber-50 border-amber-200 text-amber-700",
    tepid: "bg-sky-50 border-sky-200 text-sky-700",
    cold: "bg-zinc-100 border-zinc-200 text-zinc-600",
  };
  const tierLabel = { hot: "Caldo", warm: "Tiepido", tepid: "Tiepido-basso", cold: "Freddo" };

  const compColors = {
    molto_competitivo: "bg-emerald-100 text-emerald-700 border-emerald-200",
    competitivo: "bg-sky-100 text-sky-700 border-sky-200",
    in_linea: "bg-zinc-100 text-zinc-700 border-zinc-200",
    fuori_mercato: "bg-rose-100 text-rose-700 border-rose-200",
    non_quotato: "bg-zinc-50 text-zinc-400 border-zinc-100",
  };
  const compLabel = {
    molto_competitivo: "molto competitivo",
    competitivo: "competitivo",
    in_linea: "in linea",
    fuori_mercato: "fuori mercato",
    non_quotato: "non quotato",
  };

  const fmtPct = (v) => v === null || v === undefined ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center md:justify-end" onClick={onClose} data-testid="crm-lead-drawer">
      <div className="bg-white w-full md:max-w-lg md:h-full overflow-y-auto rounded-t-2xl md:rounded-t-none md:rounded-l-2xl p-5" onClick={(e) => e.stopPropagation()}>
        {!data ? (
          <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Caricamento...</div>
        ) : (
          <>
            <h3 className="text-lg font-semibold mb-2">{data.customer?.anagrafica?.nome || data.customer?.anagrafica?.ragione_sociale || "—"}</h3>
            <p className="text-xs text-zinc-500 mb-3">Lead {leadId.slice(0, 8)} · {TIPO_RICHIESTA[data.lead.tipo_richiesta]}</p>
            <div className="space-y-2 text-sm">
              <p><strong>Stato:</strong> {data.lead.stato_pipeline}</p>
              {data.lead.premio_stimato_eur && <p><strong>Premio stimato:</strong> €{data.lead.premio_stimato_eur}</p>}
              <p><strong>Email:</strong> {data.customer?.anagrafica?.email || "—"}</p>
              <p><strong>Telefono:</strong> {data.customer?.anagrafica?.telefono || "—"}</p>
            </div>

            {/* === Sezione Preventivass (CRM-6.3) === */}
            <PreventivassSection lead={data.lead} customer={data.customer}
              onAppetiteRefresh={(p) => setAppetite(p)} />

            {/* === Market Appetite Index V2 === */}
            <div className="mt-5 border-t border-zinc-100 pt-4" data-testid="crm-appetite-section">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" /> Market Appetite
                  <span className="text-[10px] font-normal text-zinc-400">v2.0</span>
                </p>
                <button onClick={() => setShowForm((s) => !s)}
                  data-testid="crm-appetite-toggle-form-btn"
                  className="text-xs px-2 py-1 rounded bg-zinc-900 text-white hover:bg-zinc-800">
                  {showForm ? "Chiudi form" : (appetite ? "Ricalcola" : "Calcola")}
                </button>
              </div>

              {showForm && (
                <MarketAppetiteForm leadId={leadId}
                  onComputed={(payload) => { setAppetite(payload); setShowForm(false); }} />
              )}

              {appetite && !showForm && (
                <div className="space-y-3" data-testid="crm-appetite-result">
                  {/* Score grande + tier */}
                  <div className="flex items-center gap-2">
                    <div className="text-3xl font-bold text-zinc-900" data-testid="crm-appetite-fit-score">{appetite.fit_score}</div>
                    <div className="text-xs text-zinc-500">/ 100</div>
                    <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium border ${tierColors[appetite.tier]}`}
                      data-testid="crm-appetite-tier">{tierLabel[appetite.tier]}</span>
                  </div>
                  <div className="w-full h-2 bg-zinc-100 rounded overflow-hidden">
                    <div className={`h-full ${appetite.tier === "hot" ? "bg-rose-400" : appetite.tier === "warm" ? "bg-amber-400" : appetite.tier === "tepid" ? "bg-sky-400" : "bg-zinc-400"}`}
                      style={{ width: `${appetite.fit_score}%` }} />
                  </div>

                  {/* Indicatori competitivita' */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded border border-zinc-200 p-2 text-xs" data-testid="crm-appetite-indic-prima">
                      <p className="text-zinc-500 text-[10px]">Prima (Great Lakes)</p>
                      <span className={`mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] border ${compColors[appetite.indicatore_competitivita_prima]}`}>
                        {compLabel[appetite.indicatore_competitivita_prima]}
                      </span>
                      <p className="text-[10px] text-zinc-500 mt-1">gap vs minimo: <strong>{fmtPct(appetite.gap_prima_pct)}</strong></p>
                    </div>
                    <div className="rounded border border-zinc-200 p-2 text-xs" data-testid="crm-appetite-indic-rm">
                      <p className="text-zinc-500 text-[10px]">Reale Mutua</p>
                      <span className={`mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] border ${compColors[appetite.indicatore_competitivita_reale_mutua]}`}>
                        {compLabel[appetite.indicatore_competitivita_reale_mutua]}
                      </span>
                      <p className="text-[10px] text-zinc-500 mt-1">gap vs Prima: <strong>{fmtPct(appetite.gap_reale_mutua_vs_prima_pct)}</strong></p>
                      <p className="text-[10px] text-zinc-500">gap vs minimo: <strong>{fmtPct(appetite.gap_reale_mutua_vs_minimo_pct)}</strong></p>
                    </div>
                  </div>

                  {/* Suggerimento commerciale (banner) */}
                  {appetite.suggerimento_commerciale && (
                    <div className="rounded border-l-4 border-amber-400 bg-amber-50 p-2.5 text-xs text-amber-900"
                      data-testid="crm-appetite-suggerimento">
                      <p className="font-semibold mb-0.5">Suggerimento commerciale</p>
                      <p>{appetite.suggerimento_commerciale}</p>
                    </div>
                  )}

                  {/* Motivazione */}
                  {appetite.motivazione_sintetica && (
                    <div className="text-xs text-zinc-700 bg-zinc-50 rounded p-2" data-testid="crm-appetite-motivazione">
                      <p className="font-medium mb-0.5 text-[11px] text-zinc-600">Motivazione</p>
                      <p>{appetite.motivazione_sintetica}</p>
                    </div>
                  )}

                  {/* Legal carriers */}
                  {appetite.legal_carriers && (
                    <div className="text-[10px] text-zinc-500" data-testid="crm-appetite-legal-carriers">
                      {appetite.legal_carriers.prima && (
                        <p><strong className="text-zinc-700">Prima:</strong> {appetite.legal_carriers.prima.join(" + ")}</p>
                      )}
                      {appetite.legal_carriers.reale_mutua && (
                        <p><strong className="text-zinc-700">Reale Mutua:</strong> {appetite.legal_carriers.reale_mutua.join(" + ")}</p>
                      )}
                    </div>
                  )}

                  {/* Disclaimer */}
                  <p className="text-[10px] text-zinc-400 italic leading-snug border-l-2 border-zinc-200 pl-2"
                     data-testid="crm-appetite-disclaimer">
                    {appetite.disclaimer}
                  </p>

                  {/* Fonti */}
                  <p className="text-[10px] text-zinc-400">
                    Fonti: {appetite.fonti_dati?.join(", ")}
                  </p>

                  {/* Storico */}
                  <button onClick={loadHistory} data-testid="crm-appetite-history-btn"
                    className="text-[11px] text-zinc-500 underline hover:text-zinc-700">
                    Vedi storico snapshot
                  </button>
                  {showHistory && (
                    <div className="text-[11px] bg-zinc-50 rounded p-2 max-h-32 overflow-y-auto"
                      data-testid="crm-appetite-history">
                      <p className="font-medium mb-1">Storico ({history.length})</p>
                      {history.length === 0 ? (
                        <p className="text-zinc-400 italic">Nessuno snapshot precedente.</p>
                      ) : (
                        history.map((h, i) => (
                          <div key={i} className="flex justify-between border-b border-zinc-100 py-0.5 last:border-0">
                            <span>{h.calcolato_at ? new Date(h.calcolato_at).toLocaleString("it-IT") : "—"}</span>
                            <span className="font-medium">{h.fit_score} · {tierLabel[h.tier]}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {!appetite && !showForm && (
                <p className="text-xs text-zinc-400 italic">Non calcolato. Clicca "Calcola" per inserire i parametri benchmark.</p>
              )}
            </div>

            <div className="mt-4">
              <p className="text-xs font-medium mb-1.5 text-zinc-700">Note ({(data.lead.note || []).length})</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {(data.lead.note || []).map((n, i) => (
                  <div key={i} className="text-xs bg-zinc-50 rounded p-2">
                    <p className="text-zinc-500 mb-0.5">{n.user_id} · <Calendar className="w-3 h-3 inline" /> {timeAgo(n.ts)}</p>
                    <p>{n.content}</p>
                  </div>
                ))}
                {(data.lead.note || []).length === 0 && <p className="text-xs text-zinc-400 italic">Nessuna nota</p>}
              </div>
              <div className="mt-2 flex gap-1">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nuova nota..."
                  data-testid="crm-lead-nota-input"
                  className="flex-1 text-sm px-2 py-1.5 rounded border border-zinc-200" />
                <button onClick={aggiungiNota} disabled={!note.trim()} data-testid="crm-lead-nota-add"
                  className="px-3 py-1.5 text-sm rounded bg-zinc-900 text-white disabled:opacity-50">+
                </button>
              </div>
            </div>
            <button onClick={onClose} className="mt-4 w-full py-2 text-sm rounded bg-zinc-100 hover:bg-zinc-200">Chiudi</button>
          </>
        )}
      </div>
    </div>
  );
}
